// TvCagriEkrani.js
// Konum: src/TvCagriEkrani.js — Route: /tv/:restaurantId

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_API_URL;

export default function TvCagriEkrani() {
  const { restaurantId } = useParams();
  const [calls, setCalls] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [baglantiSorunu, setBaglantiSorunu] = useState(false);
  const audioRef = useRef(null);
  const knownIds = useRef(new Set());
  const ardArdaHataSayisi = useRef(0);

  // İşletme logosunu/videosunu bir kere çek (sık değişmez, ağır bir polling gerekmiyor)
  useEffect(() => {
    fetch(`${BACKEND}/call-waiter/business/${restaurantId}`)
      .then((res) => res.json())
      .then((data) => {
        setLogoUrl(data?.logoUrl || null);
        setVideoUrl(data?.videoUrl || null);
      })
      .catch((err) => console.error('İşletme bilgisi alınamadı:', err));
  }, [restaurantId]);

  useEffect(() => {
    const fetchCalls = async () => {
      try {
        const res = await fetch(`${BACKEND}/call-waiter/call/active/${restaurantId}`);
        const data = await res.json();

        // Bağlantı sağlıklıysa hata sayacını sıfırla, uyarı varsa kaldır
        ardArdaHataSayisi.current = 0;
        setBaglantiSorunu(false);

        const hasNewCall = data.some((c) => !knownIds.current.has(c.id));
        if (hasNewCall && soundEnabled && audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch((err) => console.error('Ses çalma hatası:', err));
        }

        knownIds.current = new Set(data.map((c) => c.id));
        setCalls(data);
      } catch (err) {
        console.error('Çağrılar çekilemedi:', err);
        // 3 ardışık başarısız istekten sonra (yaklaşık 6 sn) ekrana uyarı bas —
        // tek seferlik ufak bir ağ takılması için hemen alarm vermesin
        ardArdaHataSayisi.current += 1;
        if (ardArdaHataSayisi.current >= 3) setBaglantiSorunu(true);
      }
    };

    fetchCalls();
    const interval = setInterval(fetchCalls, 2000);
    return () => clearInterval(interval);
  }, [restaurantId, soundEnabled]);

  const enableSound = () => {
    if (audioRef.current) {
      audioRef.current.play().then(() => {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }).catch((err) => console.error('Ses etkinleştirme hatası:', err));
    }
    setSoundEnabled(true);
  };

  const hasActiveCalls = calls.length > 0;
  const primaryCall = calls[0];

  return (
    <div
      onClick={!soundEnabled ? enableSound : undefined}
      className={`min-h-screen w-full flex items-center justify-center relative overflow-hidden transition-colors duration-500 ${
        hasActiveCalls ? 'bg-gradient-to-br from-orange-500 via-red-500 to-red-700 animate-pulse' : 'bg-gradient-to-br from-gray-900 to-slate-950'
      }`}
    >
      <audio ref={audioRef} src="/sounds/call-alert.mp3" preload="auto" />

      {/* Video her zaman DOM'da kalır ve oynamaya devam eder — çağrı gelince sadece
          görünürlüğü kapatılır (unmount edilmez), böylece çağrı bitince kaldığı
          sahneden devam eder, baştan başlamaz. */}
      {videoUrl && (
        <video
          src={videoUrl}
          autoPlay
          loop
          muted
          playsInline
          className={`absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-500 ${
            hasActiveCalls ? 'opacity-0' : 'opacity-100'
          }`}
        />
      )}

      {baglantiSorunu && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-red-600/90 text-white px-5 py-2.5 rounded-full text-sm font-semibold z-20 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          Bağlantı sorunu — internet/sunucu kontrol edilsin
        </div>
      )}

      {!soundEnabled && (
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 bg-white/10 text-white px-6 py-3 rounded-full text-base z-20">
          Sesi etkinleştirmek için ekrana dokunun
        </div>
      )}

      {!hasActiveCalls ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-8">
          {!videoUrl && (
            logoUrl ? (
              <img src={logoUrl} alt="Logo" className="max-h-48 max-w-xs object-contain" />
            ) : (
              <div className="flex items-center gap-3 text-slate-400 text-xl font-semibold tracking-widest uppercase">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400" />
                </span>
                Çağrı bekleniyor
              </div>
            )
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 relative z-10">
          <div className="absolute inset-0 -m-32 rounded-full border-4 border-white/40 animate-ping" />
          <div className="text-8xl mb-2 animate-bounce">🔔</div>
          <div className="text-white/90 text-3xl font-bold tracking-widest uppercase">Masa</div>
          <div className="text-white text-[220px] leading-none font-black drop-shadow-2xl">
            {primaryCall.tableNumber}
          </div>
          {calls.length > 1 && (
            <div className="text-white/90 text-2xl font-semibold mt-6">
              Ayrıca bekleyen: {calls.slice(1).map((c) => c.tableNumber).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}